// One claim list, at /p/<practice>/lists/<id>: a top bar with the title (editable),
// "Copy link", the notes (autosaved on blur, debounced), the open/closed toggle and
// "Back to claims"; below it the normal claims ledger narrowed to the list's claims, so
// every column, filter and the claim drawer work as they do on the Claims page.
import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ClaimLists.css";
import ClaimsLedgerPage from "../../pages/ClaimsLedgerPage";
import { fetchClaimList, listUrl, updateClaimList } from "./api";
import { formatUpdated } from "./ClaimListsStrip";

export const NOTES_SAVE_DELAY_MS = 400;

export default function ClaimListPage({
  selectedPractice,
  listId,
  readOnly,
  isAdmin,
  onBack,
  onOpenList,
  onPageChange,
}) {
  const practiceId = selectedPractice?.id;
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const savedNotesRef = useRef("");
  const notesTimerRef = useRef(null);
  // The notes as typed, for the unmount flush below (the effect cannot see state).
  const notesRef = useRef("");
  notesRef.current = notes;

  const load = useCallback(async () => {
    if (!practiceId || !listId) return;
    setError(null);
    try {
      const row = await fetchClaimList(practiceId, listId);
      setList(row);
      setTitle(row.title);
      setNotes(row.notes || "");
      savedNotesRef.current = row.notes || "";
    } catch (err) {
      setError(err.status === 404 ? "This list does not exist for this practice." : err.message);
    }
  }, [practiceId, listId]);

  useEffect(() => {
    load();
  }, [load]);

  // Leaving the page (Back, the sidebar) blurs the notes and unmounts before the
  // debounced save fires: send the pending save now rather than dropping the typing.
  useEffect(
    () => () => {
      if (!notesTimerRef.current) return;
      clearTimeout(notesTimerRef.current);
      notesTimerRef.current = null;
      if (notesRef.current !== savedNotesRef.current) {
        updateClaimList(practiceId, listId, { notes: notesRef.current }).catch(() => {
          /* the page is gone; nothing left to show the error on */
        });
      }
    },
    [practiceId, listId],
  );

  const save = async (changes, okText) => {
    setSaving(true);
    try {
      const row = await updateClaimList(practiceId, listId, changes);
      setList(row);
      if (okText) setMessage({ type: "success", text: okText });
      return row;
    } catch (err) {
      setMessage({ type: "error", text: err.message });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveTitle = async () => {
    const next = title.trim();
    if (!list || next === list.title) return;
    if (!next) {
      setTitle(list.title);
      setMessage({ type: "error", text: "A list needs a title." });
      return;
    }
    await save({ title: next });
  };

  const scheduleNotesSave = () => {
    clearTimeout(notesTimerRef.current);
    notesTimerRef.current = setTimeout(async () => {
      notesTimerRef.current = null;
      const value = notesRef.current;
      if (value === savedNotesRef.current) return;
      const row = await save({ notes: value });
      if (row) savedNotesRef.current = row.notes || "";
    }, NOTES_SAVE_DELAY_MS);
  };

  const toggleStatus = () =>
    save(
      { status: list.status === "open" ? "closed" : "open" },
      list.status === "open" ? "List closed." : "List reopened.",
    );

  const copyLink = async () => {
    const url = listUrl(practiceId, listId);
    try {
      await navigator.clipboard.writeText(url);
      setMessage({ type: "success", text: "Link copied." });
    } catch (err) {
      setMessage({ type: "error", text: `Could not copy. The link is ${url}` });
    }
  };

  if (error) {
    return (
      <div className="claim-list-page">
        <div className="claim-list-message error">
          <span>{error}</span>
        </div>
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          Back to claims
        </button>
      </div>
    );
  }
  if (!list) {
    return (
      <div className="claim-list-page">
        <div className="ledger-loading">
          <div className="spinner"></div>
          <span>Loading list...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="claim-list-page" data-testid="claim-list-page">
      <div className="claim-list-topbar">
        <div className="claim-list-topbar-row">
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            ← Back to claims
          </button>
          <input
            className="claim-list-title-input"
            aria-label="List title"
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
          />
          <span className={`claim-list-status ${list.status}`}>{list.status}</span>
          <span className="claim-list-meta">
            {list.count} claim{list.count === 1 ? "" : "s"} · updated{" "}
            {formatUpdated(list.updated_at)}
            {saving ? " · saving…" : ""}
          </span>
          <div className="claim-list-topbar-actions">
            <button type="button" className="btn btn-secondary" onClick={copyLink}>
              Copy link
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={toggleStatus}
              disabled={saving}
            >
              {list.status === "open" ? "Close list" : "Reopen list"}
            </button>
          </div>
        </div>
        <textarea
          className="claim-list-notes"
          aria-label="Notes"
          placeholder="Notes: what to do with these claims, and what has been done."
          value={notes}
          maxLength={20000}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={scheduleNotesSave}
          rows={3}
        />
        {message && (
          <div className={`claim-list-message ${message.type}`}>
            <span>{message.text}</span>
            <button
              type="button"
              className="message-close"
              onClick={() => setMessage(null)}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        )}
      </div>
      <ClaimsLedgerPage
        key={`list-${listId}-${list.count}`}
        selectedPractice={selectedPractice}
        readOnly={readOnly}
        isAdmin={isAdmin}
        onPageChange={onPageChange}
        claimListId={listId}
        onOpenList={onOpenList}
        onListsChanged={load}
      />
    </div>
  );
}
